// Privacy compliance configuration for different jurisdictions
export interface PrivacyRequirements {
  minAge: number;
  parentalConsentAge: number;
  explicitConsentRequired: boolean;
  dataPortabilityRequired: boolean;
  rightToDeleteRequired: boolean;
  optOutRequired: boolean;
  additionalNotices: string[];
  specificRequirements?: {
    [key: string]: any;
  };
}

// State-specific privacy requirements
const STATE_REQUIREMENTS: Record<string, Partial<PrivacyRequirements>> = {
  // California - CCPA/CPRA
  CA: {
    minAge: 13, // CCPA considers under 13 as requiring parental consent
    parentalConsentAge: 16, // CCPA requires opt-in consent for sale of data under 16
    explicitConsentRequired: true,
    dataPortabilityRequired: true,
    rightToDeleteRequired: true,
    optOutRequired: true,
    additionalNotices: [
      'California residents have the right to opt-out of the sale of personal information',
      'You have the right to know what personal information is collected',
      'You have the right to delete personal information',
      'You have the right to non-discrimination for exercising privacy rights',
    ],
    specificRequirements: {
      doNotSellLink: true,
      financialIncentiveDisclosure: true,
    },
  },
  // Virginia - VCDPA
  VA: {
    parentalConsentAge: 18,
    explicitConsentRequired: true,
    dataPortabilityRequired: true,
    rightToDeleteRequired: true,
    optOutRequired: true,
    additionalNotices: ['Virginia residents have additional privacy rights under VCDPA'],
  },
  // Colorado - CPA
  CO: {
    parentalConsentAge: 13,
    explicitConsentRequired: true,
    dataPortabilityRequired: true,
    rightToDeleteRequired: true,
    optOutRequired: true,
    additionalNotices: ['Colorado residents have privacy rights under the Colorado Privacy Act'],
  },
  // Connecticut - CTDPA
  CT: {
    parentalConsentAge: 16,
    explicitConsentRequired: true,
    dataPortabilityRequired: true,
    rightToDeleteRequired: true,
    optOutRequired: true,
    additionalNotices: ['Connecticut residents have privacy rights under CTDPA'],
  },
  // Utah - UCPA
  UT: {
    parentalConsentAge: 13,
    dataPortabilityRequired: true,
    rightToDeleteRequired: true,
    optOutRequired: true,
    additionalNotices: ['Utah residents have privacy rights under UCPA'],
  },
};

// Default requirements (COPPA federal baseline)
const DEFAULT_REQUIREMENTS: PrivacyRequirements = {
  minAge: 14, // Your business decision to be 14+
  parentalConsentAge: 18, // Conservative approach
  explicitConsentRequired: false,
  dataPortabilityRequired: false,
  rightToDeleteRequired: true,
  optOutRequired: false,
  additionalNotices: [],
};

// Get privacy requirements based on location
export function getPrivacyRequirements(state?: string): PrivacyRequirements {
  if (!state) {
    return DEFAULT_REQUIREMENTS;
  }

  const stateReqs = STATE_REQUIREMENTS[state.toUpperCase()];
  if (!stateReqs) {
    return DEFAULT_REQUIREMENTS;
  }

  // Merge state requirements with defaults, using the most restrictive option
  return {
    minAge: Math.max(stateReqs.minAge || DEFAULT_REQUIREMENTS.minAge, DEFAULT_REQUIREMENTS.minAge),
    parentalConsentAge: Math.max(
      stateReqs.parentalConsentAge || DEFAULT_REQUIREMENTS.parentalConsentAge,
      DEFAULT_REQUIREMENTS.parentalConsentAge
    ),
    explicitConsentRequired:
      stateReqs.explicitConsentRequired || DEFAULT_REQUIREMENTS.explicitConsentRequired,
    dataPortabilityRequired:
      stateReqs.dataPortabilityRequired || DEFAULT_REQUIREMENTS.dataPortabilityRequired,
    rightToDeleteRequired:
      stateReqs.rightToDeleteRequired || DEFAULT_REQUIREMENTS.rightToDeleteRequired,
    optOutRequired: stateReqs.optOutRequired || DEFAULT_REQUIREMENTS.optOutRequired,
    additionalNotices: [
      ...DEFAULT_REQUIREMENTS.additionalNotices,
      ...(stateReqs.additionalNotices || []),
    ],
    specificRequirements: stateReqs.specificRequirements,
  };
}

// Determine user location (simplified - in production, use a proper geolocation service)
export async function detectUserLocation(): Promise<{ state?: string; country: string }> {
  // For MVP, we'll default to US without state detection
  // In production, you would implement one of these:
  // 1. IP geolocation service (e.g., ipapi, MaxMind)
  // 2. Browser geolocation API (requires user permission)
  // 3. Vercel Edge Functions with geo headers
  // 4. Let users select their state manually

  // Check if we have a previously selected state in localStorage
  try {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      const parsed = JSON.parse(savedLocation);
      if (parsed.state && parsed.country) {
        return parsed;
      }
    }
  } catch {
    // Ignore parsing errors
  }

  // Default to US without state specification
  // This ensures COPPA compliance at the federal level
  return { country: 'US' };
}

// Privacy notice component props
export interface PrivacyNoticeProps {
  state?: string;
  age: number;
  onAccept: () => void;
  onReject: () => void;
}

// Check if additional consent is needed for data practices
export function needsAdditionalConsent(state: string | undefined, age: number): boolean {
  const requirements = getPrivacyRequirements(state);

  // CCPA requires opt-in for "sale" of data for users under 16
  if (state === 'CA' && age < 16) {
    return true;
  }

  // Other states may have specific requirements
  return requirements.explicitConsentRequired && age < requirements.parentalConsentAge;
}

// Generate appropriate privacy policy link based on location
export function getPrivacyPolicyUrl(state?: string): string {
  // In production, you might have state-specific privacy policies
  const baseUrl = '/privacy';

  if (state && STATE_REQUIREMENTS[state.toUpperCase()]) {
    return `${baseUrl}?state=${state.toLowerCase()}`;
  }

  return baseUrl;
}

// Get age verification message based on location
export function getAgeVerificationMessage(state?: string): string {
  const requirements = getPrivacyRequirements(state);

  if (state === 'CA') {
    return `You must be at least ${requirements.minAge} years old to create an account. California residents under 16 require parental consent for certain data uses.`;
  }

  return `You must be at least ${requirements.minAge} years old to create an account.`;
}

// Format location-specific consent text
export function getConsentText(
  state?: string,
  age?: number
): {
  primary: string;
  additional: string[];
} {
  const requirements = getPrivacyRequirements(state);

  let primary = 'By creating an account, you agree to our Terms of Service and Privacy Policy.';
  const additional: string[] = [];

  if (state === 'CA' && age && age < 16) {
    primary =
      'By creating an account, you and your parent/guardian agree to our Terms of Service and Privacy Policy.';
    additional.push(
      'California law requires parental consent for users under 16.',
      'We do not sell the personal information of users under 16.'
    );
  }

  if (requirements.optOutRequired) {
    additional.push('You have the right to opt-out of certain data uses.');
  }

  if (requirements.rightToDeleteRequired) {
    additional.push('You have the right to request deletion of your personal information.');
  }

  return { primary, additional };
}
